import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CostInsightPage } from '@internal/plugin-cost-insight';
import { FleetInsightPage } from '@internal/plugin-fleet-insight';
import { AiAssistantPage } from '@internal/plugin-ai-assistant';
import { MattaverseOrchestrationPage } from '@internal/plugin-mattaverse-orchestration';
import { TestEquipmentsPage } from '@internal/plugin-test-equipments';
import { AdminPage } from '@internal/plugin-admin';
import { kdsLightTheme } from './themes/kdsLightTheme';
import { UnifiedThemeProvider } from '@backstage/theme';
import {
  MetricsPage,
  CloudCompetenceMetricsPage,
  RepositoryQualityViewPage,
  TeamQualityViewPage,
  TribeQualityKpisPage,
  TribeQualityViewPage,
} from '@internal/plugin-metrics';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'azure-auth-provider',
          title: 'KONE SSO',
          message: 'Sign in using KONE SSO',
          apiRef: microsoftAuthApiRef,
        }}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/metrics" element={<MetricsPage />} />
    <Route path="/metrics/cloud-competence" element={<CloudCompetenceMetricsPage />} />
    <Route path="/metrics/repository-quality" element={<RepositoryQualityViewPage />} />
    <Route path="/metrics/team-quality" element={<TeamQualityViewPage />} />
    <Route path="/metrics/tribe-quality-kpis" element={<TribeQualityKpisPage />} />
    <Route path="/metrics/tribe-quality-view" element={<TribeQualityViewPage />} />
    <Route path="/cost-insight" element={<CostInsightPage />} />
    <Route path="/fleet-insight" element={<FleetInsightPage />} />
    <Route path="/ai-assistant" element={<AiAssistantPage />} />
    <Route path="/admin" element={<AdminPage />} />
    <Route path="/mattaverse-orchestration" element={<MattaverseOrchestrationPage />} />
    <Route path="/test-equipments/*" element={<TestEquipmentsPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <UnifiedThemeProvider theme={kdsLightTheme}>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </UnifiedThemeProvider>,
);
